
import React, { useEffect, useRef } from 'react';
import { Mic, X, Wifi } from 'lucide-react';

interface Props {
  isActive: boolean;
  onClose: () => void;
  audioAnalyser: AnalyserNode | null;
}

export const VoiceOverlay: React.FC<Props> = ({ isActive, onClose, audioAnalyser }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    if (!isActive || !canvasRef.current || !audioAnalyser) {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Resize canvas to match display size
    const resizeCanvas = () => {
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const bufferLength = audioAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
        animationRef.current = requestAnimationFrame(draw);
        audioAnalyser.getByteFrequencyData(dataArray);

        // Clear canvas
        const width = canvas.width / (window.devicePixelRatio || 1);
        const height = canvas.height / (window.devicePixelRatio || 1);
        ctx.clearRect(0, 0, width, height);

        const centerX = width / 2;
        const centerY = height / 2;

        // 1. Calculate Volume (RMS-ish)
        let sum = 0;
        // Focus on lower-mid frequencies for volume pulse
        for (let i = 0; i < bufferLength / 2; i++) {
            sum += dataArray[i];
        }
        const average = sum / (bufferLength / 2);
        const volume = average / 255; // 0 to 1

        // 2. Core Glow (Pulses with Volume)
        const baseRadius = 60;
        const maxRadius = 100;
        const currentRadius = baseRadius + (maxRadius - baseRadius) * volume;

        const gradient = ctx.createRadialGradient(centerX, centerY, baseRadius * 0.5, centerX, centerY, currentRadius * 1.5);
        gradient.addColorStop(0, 'rgba(99, 102, 241, 0.9)'); // Indigo 500
        gradient.addColorStop(0.5, 'rgba(168, 85, 247, 0.4)'); // Purple 500
        gradient.addColorStop(1, 'rgba(236, 72, 153, 0)'); // Pink 500 transparent

        ctx.beginPath();
        ctx.arc(centerX, centerY, currentRadius * 1.5, 0, 2 * Math.PI);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Solid Core
        ctx.beginPath();
        ctx.arc(centerX, centerY, currentRadius * 0.6, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.fill();

        // 3. Frequency Bars (Circular)
        const bars = 64; // Number of bars
        const radius = currentRadius + 10;
        const step = (Math.PI * 2) / bars;

        ctx.beginPath();
        for (let i = 0; i < bars; i++) {
            // Map bar index to frequency index (logarithmic or linear)
            // Use lower half of spectrum for better visuals usually
            const dataIndex = Math.floor((i / bars) * (bufferLength * 0.7)); 
            const value = dataArray[dataIndex];
            const barHeight = (value / 255) * 80 * (volume + 0.5); // Scale by volume too for impact

            const angle = i * step;
            const x1 = centerX + Math.cos(angle) * radius;
            const y1 = centerY + Math.sin(angle) * radius;
            const x2 = centerX + Math.cos(angle) * (radius + barHeight + 2); // Minimum length
            const y2 = centerY + Math.sin(angle) * (radius + barHeight + 2);

            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
        }
        
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.3 + volume * 0.7})`;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();

        // 4. Outer Ring (Subtle rotation or static)
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius + 90, 0, 2 * Math.PI);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.stroke();
    };

    draw();

    return () => {
        window.removeEventListener('resize', resizeCanvas);
        cancelAnimationFrame(animationRef.current);
    };
  }, [isActive, audioAnalyser]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white animate-fade-in">
      {/* Background Ambience */}
      <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
         <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
         <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-indigo-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
         <div className="absolute -bottom-8 left-1/3 w-96 h-96 bg-pink-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-12 text-center p-6 w-full h-full justify-center">
        
        <div className="space-y-2 absolute top-12 left-0 right-0">
            <h2 className="text-3xl font-light tracking-wide font-sans">Future Self Mode</h2>
            <p className="text-indigo-200 text-sm">Close your eyes. Speak freely. Kai is listening.</p>
        </div>

        {/* Dynamic Canvas Visualizer */}
        <div className="relative w-full max-w-lg aspect-square flex items-center justify-center">
             <canvas 
                ref={canvasRef} 
                className="w-full h-full"
                style={{ width: '100%', height: '100%' }}
             />
             {/* Mic Icon Overlay (Centered on top of canvas) */}
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none mix-blend-overlay">
                <Mic size={32} className="text-white opacity-50" />
             </div>
        </div>

        <button 
            onClick={onClose}
            className="group flex items-center gap-2 px-8 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full transition-all border border-white/10 absolute bottom-12"
        >
            <X size={18} />
            <span className="font-medium">End Session</span>
        </button>

      </div>

      <div className="absolute bottom-8 right-8 flex items-center gap-2 text-xs text-indigo-300 opacity-60">
        <Wifi size={14} />
        <span>Live Connection Active</span>
      </div>
    </div>
  );
};
